import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import ImagemProduto from '../components/ImagemProduto';
import './Catalogo.css';

const norm = s => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/**
 * Admin dos destaques do catálogo (curadoria da diretoria). Define os pisos
 * "Recomendados" que aparecem no topo da vitrine pros arquitetos. Protegida pela
 * permissão `catalogo_destaques`.
 */
export default function CatalogoDestaques() {
  const [produtos, setProdutos] = useState([]);
  const [destaques, setDestaques] = useState([]); // codpros na ordem
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [busca, setBusca] = useState('');
  const [visivel, setVisivel] = useState(false);

  const carregar = useCallback(() => {
    setCarregando(true);
    setErro('');
    Promise.all([api.get('/catalogo'), api.get('/catalogo/destaques'), api.get('/catalogo/config')])
      .then(([c, d, cfg]) => {
        setProdutos(c.data.produtos || []);
        setDestaques((d.data || []).map(x => x.codpro));
        setVisivel(!!cfg.data.visivelArquiteto);
      })
      .catch(e => setErro(e.response?.data?.error || 'Erro ao carregar'))
      .finally(() => setCarregando(false));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const toggleVisivel = async () => {
    const novo = !visivel;
    setVisivel(novo);
    try { await api.put('/catalogo/config', { visivelArquiteto: novo }); }
    catch (e) { setVisivel(!novo); setErro(e.response?.data?.error || 'Erro ao alterar visibilidade'); }
  };

  const porCod = useMemo(() => new Map(produtos.map(p => [p.codpro, p])), [produtos]);
  const destSet = useMemo(() => new Set(destaques), [destaques]);
  const listaDestaques = destaques.map(c => porCod.get(c)).filter(Boolean);

  const resultados = useMemo(() => {
    const t = norm(busca);
    if (!t) return [];
    return produtos
      .filter(p => !destSet.has(p.codpro) && norm(`${p.nome} ${p.codpro} ${p.fornecedor}`).includes(t))
      .slice(0, 8);
  }, [busca, produtos, destSet]);

  const adicionar = async (codpro) => {
    setSalvando(true);
    try { await api.post('/catalogo/destaques', { codpro }); setBusca(''); await recarregaLista(); }
    catch (e) { setErro(e.response?.data?.error || 'Erro ao adicionar'); }
    finally { setSalvando(false); }
  };

  const remover = async (codpro) => {
    setSalvando(true);
    try { await api.delete(`/catalogo/destaques/${codpro}`); await recarregaLista(); }
    catch (e) { setErro(e.response?.data?.error || 'Erro ao remover'); }
    finally { setSalvando(false); }
  };

  // Recarrega só a lista de destaques (não o catálogo inteiro — mais leve)
  const recarregaLista = async () => {
    const d = await api.get('/catalogo/destaques');
    setDestaques((d.data || []).map(x => x.codpro));
  };

  const mover = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= destaques.length) return;
    const novo = [...destaques];
    [novo[idx], novo[j]] = [novo[j], novo[idx]];
    setDestaques(novo); // otimista
    try { await api.put('/catalogo/destaques/ordem', { codpros: novo }); }
    catch { recarregaLista(); }
  };

  return (
    <div>
      <h4 className="fw-bold mb-1">
        <i className="bi bi-star-fill me-2" style={{ color: 'var(--accent)' }}></i>
        Destaques do Catálogo
      </h4>
      <p className="text-muted mb-4" style={{ fontSize: '0.88rem' }}>
        Os pisos <strong>Recomendados</strong> que aparecem no topo do catálogo pros arquitetos.
        A ordem aqui é a ordem que eles veem.
      </p>

      {erro && <div className="alert alert-danger">{erro}</div>}

      {/* Visibilidade pros arquitetos */}
      <div className="card p-3 mb-4" style={{ borderRadius: 14, borderLeft: `4px solid ${visivel ? 'var(--primary)' : '#cdd6cd'}` }}>
        <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
          <div>
            <div className="fw-bold" style={{ fontSize: '0.95rem' }}>
              <i className={`bi ${visivel ? 'bi-eye' : 'bi-eye-slash'} me-2`} style={{ color: visivel ? 'var(--primary)' : '#9aa89c' }} />
              Catálogo {visivel ? 'liberado' : 'oculto'} pros arquitetos
            </div>
            <div className="text-muted" style={{ fontSize: '0.82rem', marginTop: 2 }}>
              {visivel
                ? 'Os arquitetos veem o catálogo no portal (quem estava logado precisa entrar de novo pra ver).'
                : 'Só a equipe interna vê. Ligue quando quiser liberar pros arquitetos.'}
            </div>
          </div>
          <button type="button" role="switch" aria-checked={visivel}
            className={`cat-switch ${visivel ? 'ativo' : ''}`} onClick={toggleVisivel}>
            <span className="cat-switch-track"><span className="cat-switch-thumb" /></span>
            {visivel ? 'Liberado' : 'Liberar'}
          </button>
        </div>
      </div>

      {/* Departamentos (abas) agora ficam na Gestão do catálogo */}
      <Link to="/catalogo-gestao" className="card p-3 mb-4 text-decoration-none" style={{ borderRadius: 14 }}>
        <div className="d-flex align-items-center justify-content-between gap-3">
          <div>
            <div className="fw-bold" style={{ fontSize: '0.95rem', color: 'var(--text)' }}>
              <i className="bi bi-diagram-3 me-2" style={{ color: 'var(--primary)' }} />
              Departamentos e classes
            </div>
            <div className="text-muted" style={{ fontSize: '0.82rem', marginTop: 2 }}>
              Ligar/desligar abas, criar departamentos e dar nomes amigáveis às classes — em Gestão do catálogo.
            </div>
          </div>
          <i className="bi bi-chevron-right" style={{ color: 'var(--primary)' }} />
        </div>
      </Link>

      {/* Adicionar */}
      <div className="card p-3 mb-4" style={{ borderRadius: 14 }}>
        <label className="form-label fw-semibold mb-2" style={{ fontSize: '0.9rem' }}>Adicionar piso aos recomendados</label>
        <div className="cat-busca-wrap" style={{ maxWidth: 520 }}>
          <i className="bi bi-search" />
          <input
            className="cat-busca"
            type="search"
            placeholder="Buscar por nome, código ou fornecedor..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
            autoComplete="off"
          />
        </div>
        {resultados.length > 0 && (
          <div className="mt-2" style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 520 }}>
            {resultados.map(p => (
              <button
                key={p.codpro}
                type="button"
                className="cd-add-item"
                disabled={salvando}
                onClick={() => adicionar(p.codpro)}
              >
                <ImagemProduto codpro={p.codpro} tamanho="sm" alt={p.nome} />
                <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.86rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.nome}</div>
                  <div className="text-muted" style={{ fontSize: '0.76rem' }}>Cód {p.codpro} · {p.fornecedor}</div>
                </div>
                <i className="bi bi-plus-circle-fill" style={{ color: 'var(--primary)', fontSize: '1.2rem' }} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lista atual */}
      <h6 className="fw-bold mb-2" style={{ color: 'var(--primary-dark)' }}>
        Recomendados atuais <span className="text-muted fw-normal">({listaDestaques.length})</span>
      </h6>

      {carregando ? (
        <div className="text-center py-5"><div className="spinner-border" style={{ color: 'var(--primary)' }} /></div>
      ) : listaDestaques.length === 0 ? (
        <div className="cat-vazio"><i className="bi bi-star" />Nenhum piso recomendado ainda. Busque acima pra adicionar.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {listaDestaques.map((p, idx) => (
            <div key={p.codpro} className="cd-destaque-row">
              <span className="cd-pos">{idx + 1}</span>
              <ImagemProduto codpro={p.codpro} tamanho="sm" clicavel alt={p.nome} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '0.88rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.nome}</div>
                <div className="text-muted" style={{ fontSize: '0.77rem' }}>Cód {p.codpro} · {p.fornecedor}</div>
              </div>
              <div className="cd-acoes">
                <button type="button" title="Subir" disabled={idx === 0 || salvando} onClick={() => mover(idx, -1)}><i className="bi bi-arrow-up" /></button>
                <button type="button" title="Descer" disabled={idx === listaDestaques.length - 1 || salvando} onClick={() => mover(idx, 1)}><i className="bi bi-arrow-down" /></button>
                <button type="button" title="Remover" className="cd-remover" disabled={salvando} onClick={() => remover(p.codpro)}><i className="bi bi-trash" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
